import type {
  BoardBuilderProjections,
  BoardBuilderUpdateProjection
} from '@library/types/boardBuilder';
import {
  BoardBuilderProjectionsHeader,
  StatTypeHeader
} from '@library/components/boardBuilder';
import type {
  LeagueInfo,
  LeagueProjectionAttributes
} from '@library/types/leagues';
import { memo, useState } from 'react';
import { BoardBuilderProjectionRow } from '@bo-components/boardBuilder';
import type { BulkAutoTradeChange } from '@bo-hooks/boardBuilder/useBoardBuilderProjections';
import { DateTime } from 'luxon';
import type { FormattedGameMode } from '@library/types/gameModes';
import type { Status } from '@library/types/projection';
import { classNames } from '@library/utils';
import { isTimeBefore } from '@library/utils/dateTime';

export interface BoardBuilderProjectionRowsProps {
  confirmedGameModes: Array<FormattedGameMode>;
  durations: Array<LeagueProjectionAttributes>;
  leagueInfo: LeagueInfo;
  mainDurationId: string;
  onProjectionsChange: (projection: BoardBuilderUpdateProjection) => void;
  onBulkAutoTradeChange: ({
    autoTrade,
    statTypeId
  }: BulkAutoTradeChange) => void;
  onBulkTimeChange: ({
    durationId,
    statTypeId,
    boardTime,
    startTime
  }: {
    durationId?: string;
    statTypeId?: string;
    boardTime?: DateTime;
    startTime?: DateTime;
  }) => void;
  onToggleDurationStatTypeAllProjectionsStatus: ({
    durationId,
    statTypeId,
    status
  }: {
    durationId: string;
    statTypeId: string;
    status: Status;
  }) => void;
  projectionRows: BoardBuilderProjections;
  selectedDurationId: string;
  statTypeId: string;
  statTypeName: string;
  isDisplayTimeVisible: boolean;
}

export const BoardBuilderProjectionRows = memo(
  ({
    confirmedGameModes,
    durations,
    leagueInfo,
    mainDurationId,
    projectionRows,
    onProjectionsChange,
    onBulkAutoTradeChange,
    onBulkTimeChange,
    onToggleDurationStatTypeAllProjectionsStatus,
    selectedDurationId,
    statTypeId,
    statTypeName,
    isDisplayTimeVisible = false
  }: BoardBuilderProjectionRowsProps) => {
    const [showProjections, setShowProjections] = useState(true);

    const hasInactiveProjection = projectionRows.some(
      (projection) => projection.status === 'removed'
    );

    const hasAutoTrade = !projectionRows.some(
      (projection) => !projection.autoTrade
    );

    const isBulkAutoTradeDisabled =
      !projectionRows.length ||
      projectionRows.some((projection) => {
        const startDateTime = DateTime.fromISO(projection.startTime);
        return (
          isTimeBefore({
            dateValue: startDateTime,
            dateToCompareAgainst: DateTime.now()
          }) || projection.isPromo
        );
      });

    const initialTimes = {
      initialBoardTime: projectionRows[0]?.boardTime,
      initialStartTime: projectionRows[0]?.startTime,
      initialDisplayTime: projectionRows[0]?.displayTime
    };

    return (
      <div className="border-soFresh-140 inline-flex w-full flex-col items-start justify-start gap-4 rounded border">
        <StatTypeHeader
          allProjectionsActive={!hasInactiveProjection}
          allProjectionsAutoTrade={hasAutoTrade}
          isBulkAutoTradeDisabled={isBulkAutoTradeDisabled}
          onBulkAutoTradeChange={onBulkAutoTradeChange}
          onBulkTimeChange={onBulkTimeChange}
          onToggleDurationStatTypeAllProjectionsStatus={
            onToggleDurationStatTypeAllProjectionsStatus
          }
          onToggleShowProjections={() => setShowProjections(!showProjections)}
          initialProjectionTimes={initialTimes}
          showProjections={showProjections}
          selectedDurationId={selectedDurationId}
          statTypeId={statTypeId}
          statTypeName={statTypeName}
          isDisplayTimeVisible={isDisplayTimeVisible}
        />
        <table
          className={classNames(
            'w-full table-auto',
            showProjections ? 'inherit' : 'hidden'
          )}
        >
          <BoardBuilderProjectionsHeader
            isDisplayTimeVisible={isDisplayTimeVisible}
          />
          <tbody>
            {projectionRows.map((projection) => (
              <BoardBuilderProjectionRow
                confirmedGameModes={confirmedGameModes}
                durations={durations}
                onChange={onProjectionsChange}
                key={projection.projectionId}
                leagueInfo={leagueInfo}
                mainDurationId={mainDurationId}
                projection={projection}
                isDisplayTimeVisible={isDisplayTimeVisible}
              />
            ))}
          </tbody>
        </table>
      </div>
    );
  }
);
